import { createPortal } from 'react-dom';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import clsx from 'clsx';
import { IconButton } from 'ui-components/components/button/icon-button';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { GoToNextModalHotkey } from '../../../../components/hotkeys/go-to-next-modal';
import { GoToPreviousModalHotkey } from '../../../../components/hotkeys/go-to-previous-modal';
import { useModalManagerPortal } from '../../../../portals/hooks';
import { freightNavigationButtonsMessages } from './messages';
import style from './freight-navigation-buttons.module.css';

type FreightNavigationButtonsProps = {
  onNavigatePrevious: (() => void) | null;
  onNavigateNext: (() => void) | null;
};

export const FreightNavigationButtons = ({ onNavigatePrevious, onNavigateNext }: FreightNavigationButtonsProps) => {
  const portalTarget = useModalManagerPortal();
  // useMaxZ() uses a module-level monotonic counter; the parent container calls it first
  // (parent hooks run before child hooks), so this call gets a strictly higher value than
  // the Drawer's z-index, ensuring pointer events land on these portaled buttons.
  const zIndex = useMaxZ();

  if (!onNavigatePrevious && !onNavigateNext) {
    return null;
  }

  const zIndexStyle = { zIndex };

  // data-ignore-root-focus prevents useFocusJail from reclaiming focus when a portaled
  // button is clicked (useFocusJail checks for this attribute via .closest()).
  const buttonPair = (
    <div data-ignore-root-focus>
      <IconButton
        ariaLabelMessageDescriptor={freightNavigationButtonsMessages.navigateToPrevious}
        className={clsx(style['nav-button'], style['previous-button'])}
        disabled={!onNavigatePrevious}
        icon={faArrowLeft}
        onClick={onNavigatePrevious ?? undefined}
        style={zIndexStyle}
      />
      <IconButton
        ariaLabelMessageDescriptor={freightNavigationButtonsMessages.navigateToNext}
        className={clsx(style['nav-button'], style['next-button'])}
        disabled={!onNavigateNext}
        icon={faArrowRight}
        onClick={onNavigateNext ?? undefined}
        style={zIndexStyle}
      />
    </div>
  );

  return (
    <>
      {onNavigatePrevious ? <GoToPreviousModalHotkey onNavigate={onNavigatePrevious} /> : null}
      {onNavigateNext ? <GoToNextModalHotkey onNavigate={onNavigateNext} /> : null}
      {portalTarget ? createPortal(buttonPair, portalTarget) : buttonPair}
    </>
  );
};
